/**
 * Briefing Deck primitives.
 *
 * ★ COPIED VERBATIM from `downwind-console/src/components/deck.tsx`. Change it
 * there first, then re-copy — the same rule the console applies to
 * `DownwindMark`, and for the same reason: the deck is one design, and a
 * divergence between the operator console and the customer portal shows up as
 * two products rather than one. Unlike the RPC contract there is no hash test
 * on this file, because a drifted button is a cosmetic bug and a drifted
 * contract is a 503 — but the discipline is the same.
 *
 * The same five forms the aloftwx.com marketing site is built from: the strip,
 * the band, the field cell, the tag, and the marker. If a screen seems to need
 * a sixth shape, it almost certainly wants to be one of these instead — that is
 * the whole reason the site, the console and this portal read as one product
 * rather than as three teams.
 *
 * What the portal is allowed to differ on is POSTURE, not form. The console is
 * dense because density is the feature for somebody who lives in it; these
 * screens use the same forms with more air, because the reader is a customer
 * who visits four times a year.
 *
 * The visual definitions live in `src/styles/globals.css` (`.strip`,
 * `.fields`, `.t-field`, `.marker` …) so a route can compose them without
 * restating six utilities, and so the design has exactly one place to be
 * enforced.
 *
 * THREE TONES AND NO FOURTH. `cleared` (green) and `hold` (red) are real
 * product state. `marker` — the magenta pen — is the third, and it means
 * "somebody needs to look at this": a suspended key, a seat count at its
 * limit, a dispute waiting on a decision. It is deliberately the same ink as
 * the highlighter swipe on the site, because it means the same thing there.
 *
 * Everything here is presentational: no data fetching, no router, no state.
 */

import { cn } from "@/lib/utils";
import type { ComponentProps, ReactNode } from "react";

/* ── Marker ────────────────────────────────────────────────────────────────
   The signature. One phrase per page zone — a page with three markers has
   none. In this portal that budget is spent on the page header of a screen
   that is about a decision, and nowhere else. */

export function Marker({
  children,
  draw = false,
  className,
}: {
  children: ReactNode;
  /** Animate the swipe on load. Reserved for the top of a page. */
  draw?: boolean;
  className?: string;
}) {
  return <span className={cn("marker", draw && "marker-draw", className)}>{children}</span>;
}

/* ── Eyebrow ───────────────────────────────────────────────────────────────
   `── LIC │ SEARCH`. A mono micro-cap pair behind a short rule, printed above
   every page title and section head. The code is the route's operational
   abbreviation from `src/lib/nav.ts` — the thing an operator writes on a
   ticket — and the label says which part of it you are looking at. */

export function Eyebrow({
  code,
  label,
  className,
}: {
  code: string;
  label: string;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <span aria-hidden="true" className="h-px w-6 bg-rule-strong" />
      <span className="t-field text-ink">{code}</span>
      <span aria-hidden="true" className="h-2.5 w-px bg-rule-strong" />
      <span className="t-field">{label}</span>
    </div>
  );
}

/* ── Strip ─────────────────────────────────────────────────────────────────
   The card. A printed progress strip: squared stock with a status bar down
   the leading edge. The bar sits inside the border and is 3px wide, so
   content wants at least `pl-4`. */

export type StripTone = "neutral" | "active" | "cleared" | "hold" | "marker";

const STRIP_BAR_TONE: Record<StripTone, string> = {
  neutral: "bg-rule-strong",
  active: "bg-ink",
  cleared: "bg-cleared",
  hold: "bg-hold",
  marker: "bg-marker",
};

export function Strip({
  tone = "neutral",
  lift = false,
  bar = true,
  className,
  children,
  ...props
}: ComponentProps<"div"> & {
  tone?: StripTone;
  lift?: boolean;
  /** Set false for surfaces that aren't a record of something. */
  bar?: boolean;
}) {
  return (
    <div className={cn("strip", lift && "strip-lift", className)} {...props}>
      {bar && <span aria-hidden="true" className={cn("strip-bar", STRIP_BAR_TONE[tone])} />}
      {children}
    </div>
  );
}

/**
 * The band a strip is filed under: a mono micro-cap heading on the recessed
 * stock, ruled off from the body, with an optional trailing slot for the one
 * thing that qualifies it — a status <Tag>, a count, a single action. Every
 * panel head in this portal is this; there is no second way to print one.
 *
 * The heading is a real heading, so pairing `id` here with `aria-labelledby`
 * on the surrounding <section> gives the strip a landmark for free.
 */
export function StripHead({
  title,
  id,
  as: Heading = "h2",
  note,
  className,
  children,
}: {
  title: ReactNode;
  /** Pair with `aria-labelledby` on the wrapping <section>. */
  id?: string;
  as?: "h2" | "h3";
  /** A sentence of context under the heading. */
  note?: ReactNode;
  className?: string;
  /** Trailing slot: a <Tag>, a count, an action. */
  children?: ReactNode;
}) {
  return (
    <div className={cn("strip-head", note && "py-3.5", className)}>
      <div className="min-w-0">
        <Heading id={id} className="t-field text-ink">
          {title}
        </Heading>
        {note ? <p className="mt-1.5 text-[0.875rem] leading-snug text-ink-2">{note}</p> : null}
      </div>
      {children ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{children}</div>
      ) : null}
    </div>
  );
}

export function StripBody({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("p-4", className)} {...props}>
      {children}
    </div>
  );
}

/* ── Field cells ───────────────────────────────────────────────────────────
   Label above value, hairline-ruled like a printed form. This is how every
   number in the product is presented. Set the column count from the caller:
   <FieldGrid className="sm:grid-cols-4">. */

export function FieldGrid({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("fields grid", className)} {...props}>
      {children}
    </div>
  );
}

/** Real product state only — never decoration. See the tone note at the top. */
export type Tone = "cleared" | "hold" | "marker";

const VALUE_TONE: Record<Tone, string> = {
  cleared: "text-cleared",
  hold: "text-hold",
  marker: "text-ink",
};

export function Field({
  label,
  value,
  note,
  tone,
  mono = true,
  title,
  className,
}: {
  label: string;
  value: ReactNode;
  /** A short qualifier under the value — a unit, a count, a source. */
  note?: ReactNode;
  tone?: Tone;
  /** Set false for prose values (an edition name) that are not data. */
  mono?: boolean;
  title?: string;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0 px-4 py-3.5", className)}>
      <div className="t-field">{label}</div>
      <div
        title={title}
        className={cn(
          "mt-1.5 truncate text-[1rem] font-medium",
          mono && "t-data",
          tone ? VALUE_TONE[tone] : "text-ink",
        )}
      >
        {/* A value the pen has been run through carries the swipe, not a
            fourth hue — the marker is the deck's "look here". */}
        {tone === "marker" ? <Marker>{value}</Marker> : value}
      </div>
      {note ? <div className="mt-1 text-[0.8125rem] leading-snug text-ink-2">{note}</div> : null}
    </div>
  );
}

/* ── Leader row ────────────────────────────────────────────────────────────
   Label, dotted rule, value — the way a printed release ties a field to its
   number across a gap. For short vertical lists where a field grid would be
   four cells of mostly whitespace. */

export function LeaderRow({
  label,
  value,
  tone,
  title,
}: {
  label: string;
  value: ReactNode;
  tone?: Tone;
  title?: string;
}) {
  return (
    <div className="flex w-full items-baseline">
      <span className="t-field shrink-0">{label}</span>
      <span aria-hidden="true" className="leader" />
      <span
        title={title}
        className={cn(
          "t-data min-w-0 shrink truncate text-[0.875rem]",
          tone ? VALUE_TONE[tone] : "text-ink",
        )}
      >
        {value}
      </span>
    </div>
  );
}

/* ── Status ────────────────────────────────────────────────────────────────
   A round dot, because a dot is what a board uses for a lamp. It never
   carries meaning alone — every dot in this portal sits inside a <Tag> or
   beside a word. */

export function StatusDot({
  tone,
  pulse = false,
  className,
}: {
  tone?: Tone;
  /** Slow beacon. For a live reading only, never for a static label. */
  pulse?: boolean;
  className?: string;
}) {
  const bg = tone === "cleared" ? "bg-cleared" : tone === "hold" ? "bg-hold" : "bg-ink-3";
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block size-1.5 shrink-0 rounded-full",
        bg,
        pulse && "animate-beacon",
        className,
      )}
    />
  );
}

/**
 * Small mono tag used for statuses, editions and phase labels.
 *
 * Three redundant channels on every tag: a dot, a border colour, and the word
 * itself. No status in this portal is ever carried by colour alone — support
 * tickets arrive as monochrome screenshots more often than anyone would like.
 *
 * Carries `inline-flex`, so hide it with a variant (`max-sm:hidden`) and never
 * a bare `hidden`: Tailwind emits display utilities in a fixed order and the
 * caller's `hidden` loses to the base class.
 */
export type TagTone = "neutral" | Tone;

const TAG_TONE: Record<TagTone, string> = {
  neutral: "border-rule-strong text-ink-2",
  cleared: "border-cleared/40 text-cleared",
  hold: "border-hold/40 text-hold",
  marker: "border-marker bg-marker/20 text-ink",
};

const TAG_DOT: Record<TagTone, string> = {
  neutral: "bg-ink-3",
  cleared: "bg-cleared",
  hold: "bg-hold",
  marker: "bg-marker",
};

export function Tag({
  tone = "neutral",
  dot = true,
  pulse = false,
  className,
  children,
  ...props
}: ComponentProps<"span"> & {
  tone?: TagTone;
  /** Set false where the tag is a label (an edition) rather than a state. */
  dot?: boolean;
  pulse?: boolean;
}) {
  return (
    <span
      data-tone={tone}
      className={cn(
        "t-field inline-flex items-center gap-1.5 rounded-[2px] border px-1.5 py-0.5",
        TAG_TONE[tone],
        className,
      )}
      {...props}
    >
      {dot ? (
        <span
          aria-hidden="true"
          className={cn("size-1.5 shrink-0 rounded-full", TAG_DOT[tone], pulse && "animate-beacon")}
        />
      ) : null}
      {children}
    </span>
  );
}

/* ── Notice ────────────────────────────────────────────────────────────────
   The banner form: a strip with no card face, a tinted leading bar, a mono
   label and a sentence. Used for a state the operator must read before
   acting — a revoked license, an unimplemented endpoint, a failed load.
   Never for chrome. */

export function Notice({
  tone = "marker",
  label,
  children,
  action,
  className,
}: {
  tone?: Tone;
  label: string;
  children: ReactNode;
  /** Trailing slot for the one thing that resolves it. */
  action?: ReactNode;
  className?: string;
}) {
  const text = tone === "hold" ? "text-hold" : tone === "cleared" ? "text-cleared" : "text-ink";
  return (
    <Strip
      tone={tone}
      className={cn(
        "flex flex-wrap items-start justify-between gap-x-6 gap-y-3 px-4 py-3.5 pl-5",
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <div className={cn("t-field", text)}>{label}</div>
        <div className="mt-1.5 text-[0.9375rem] leading-snug text-ink-2">{children}</div>
      </div>
      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
    </Strip>
  );
}

/* ── Tear line ─────────────────────────────────────────────────────────────
   The perforation a release is torn along. Two per page, at most. */

export function TearLine({ className }: { className?: string }) {
  return <div aria-hidden="true" className={cn("tear", className)} />;
}

/* ── Empty ─────────────────────────────────────────────────────────────────
   The nothing-here state. Centred, quiet, and always a sentence that says why
   it is empty rather than the word "empty". */

export function Empty({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("px-4 py-10 text-center", className)}>
      <p className="mx-auto max-w-md text-[0.9375rem] leading-relaxed text-ink-2">{children}</p>
    </div>
  );
}
